/**
 * Motorcycle Quote Wizard — Version H2
 * ======================================================================
 * - All strings (section titles, help text, field labels/placeholders,
 *   UI/button labels) are externalized to motoCopy.ts.
 * - Interfaces renamed to begin with capital "I".
 * - Visual success rules: filings & motorcycle sections only turn green
 *   when their toggles are ON and required fields are complete.
 * ======================================================================
 */

import React, { useMemo, useState, ReactNode } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";
import { MOTO_COPY, IMotoCopy } from "./MotorCycle-Insurance-Quote-String-ReactNode";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "Moto-H2";
const SHOW_VERSION = true;

const TEST_MODE = false; // true ⇒ logs payload, skips network
const API_GATEWAY_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";

// DEMO controls (independent of TEST_MODE)
const DEMO_MODE_DEFAULT = true;     // initial demo state (pre-populate)
const DEMO_MODE_UI_ENABLED = true;  // show a UI checkbox to toggle demo mode

// UI behaviors
const clampPct = (p: number) => Math.min(99, Math.max(0, Math.round(p)));

/* ============================================================
   TYPES
============================================================ */
type ViewMode = "wizard" | "vertical";
export type FormShape = Record<string, unknown>;
export type SetForm = (patch: FormShape) => void;

export interface IWizardSection {
  id: string;
  title: ReactNode;
  explanation: ReactNode;
  enabled?: boolean;
  render: (form: FormShape, setForm: SetForm) => React.ReactNode;
}

/* ============================================================
   HELPERS
============================================================ */
const S = (v: unknown): string | null => {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return String(v);
};
const hasVal = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
};

/* ============================================================
   DATA
============================================================ */
const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" }, { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" }, { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" }, { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" }, { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" }, { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" }, { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" }, { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const FR44_STATES = [
  { value: "VA", label: "Virginia" },
  { value: "FL", label: "Florida" },
];

/* ============================================================
   COMPLETION SCHEMA
============================================================ */
type SectionKeys = {
  id: string;
  required: (form: FormShape) => string[];
  optional?: (form: FormShape) => string[];
};

const completionSchema: SectionKeys[] = [
  {
    id: "personal-info",
    required: () => [
      "firstName", "lastName", "dob", "email", "phone",
      "address1", "city", "state", "zip",
    ],
  },
  {
    id: "rider-history",
    required: () => ["licenseNumber", "issuingState", "yearsRiding"],
  },
  {
    id: "filings",
    required: (form) => {
      const need = (form.needFiling as string) === "yes";
      return need ? ["filingType", "filingState", "filingDuration"] : [];
    },
  },
  {
    id: "motorcycle",
    required: (form) => {
      const own = ((form.ownMoto as string) ?? "yes") === "yes";
      if (!hasVal(form.ownMoto)) return ["ownMoto"];
      if (!own) return ["ownMoto"];
      return ["ownMoto", "motoYear", "motoMake", "motoModel", "engineCC", "motoUse"]; // VIN optional
    },
  },
  {
    id: "coverage",
    required: () => [],
    optional: () => ["coverageType", "compDeductible"],
  },
  {
    id: "remarks",
    required: () => [],
    optional: () => ["remarks"],
  },
];

const requiredKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.required(form) ?? [];

const optionalKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.optional?.(form) ?? [];

const sectionIsComplete = (sectionId: string, form: FormShape): boolean => {
  const req = requiredKeysForSection(sectionId, form);
  if (req.length > 0) return req.every((k) => hasVal(form[k]));
  const opt = optionalKeysForSection(sectionId, form);
  return opt.length > 0 && opt.some((k) => hasVal(form[k]));
};

const overallCompletion = (enabledSectionIds: string[], form: FormShape): number => {
  const allReq: string[] = enabledSectionIds.flatMap((id) => requiredKeysForSection(id, form));
  const uniq = Array.from(new Set(allReq));
  if (uniq.length === 0) return 0;
  const filled = uniq.reduce((acc, k) => (hasVal(form[k]) ? acc + 1 : acc), 0);
  return (filled / uniq.length) * 100;
};

/* ============================================================
   Controls
============================================================ */
const WizardControls: React.FC<{
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
  copy?: IMotoCopy;
}> = ({ onPrev, onNext, onSubmit, isFirst, isLast, submitting, copy = MOTO_COPY }) => (
  <div className="d-flex justify-content-between">
    <Button variant="secondary" onClick={onPrev} disabled={isFirst || !!submitting}>
      {copy.buttons.previous}
    </Button>
    {isLast ? (
      <Button variant="success" onClick={onSubmit} disabled={!!submitting}>
        {submitting ? "Processing..." : copy.buttons.submit}
      </Button>
    ) : (
      <Button variant="primary" onClick={onNext} disabled={!!submitting}>
        {copy.buttons.next}
      </Button>
    )}
  </div>
);

/* ============================================================
   Dual View Wizard — Motorcycle H2
   (SR-22 & Motorcycle sections only show success when toggles are ON)
============================================================ */
export const DualViewWizard_Moto_H2: React.FC<{
  mode?: ViewMode;
  sections: IWizardSection[];
  initialForm?: FormShape;
  className?: string;
  onSubmit?: (form: FormShape) => void;
  submitting?: boolean;
  submitted?: boolean;
  copy?: IMotoCopy;
}> = ({
  mode = "wizard",
  sections,
  initialForm = {},
  className,
  onSubmit,
  submitting = false,
  submitted = false,
  copy = MOTO_COPY,
}) => {
  const enabledSections = useMemo(
    () => sections.filter((s) => s.enabled !== false),
    [sections]
  );

  const [form, setFormState] = useState<FormShape>(initialForm);
  const setForm: SetForm = (patch) => setFormState((prev) => ({ ...prev, ...patch }));

  const [current, setCurrent] = useState(0);
  const [activeId, setActiveId] = useState<string>(enabledSections[0]?.id ?? "");

  const ids = useMemo(() => enabledSections.map((s) => s.id), [enabledSections]);
  const activeIndex = Math.max(0, ids.indexOf(activeId));
  const total = Math.max(1, enabledSections.length);
  const enabledIds = enabledSections.map((s) => s.id);

  const rawPct = overallCompletion(enabledIds, form);
  const pctLabel = submitted ? 100 : clampPct(rawPct);
  const pct = submitted ? 100 : clampPct(rawPct);

  // Visual success override rules (H11a pattern):
  // - filings: only success if needFiling === "yes" AND sectionIsComplete
  // - motorcycle: only success if ownMoto === "yes" AND sectionIsComplete
  const sectionShowsSuccess = (sectionId: string): boolean => {
    const isComplete = sectionIsComplete(sectionId, form);
    if (sectionId === "filings") {
      const need = ((form.needFiling as string) ?? "no") === "yes";
      return need && isComplete;
    }
    if (sectionId === "motorcycle") {
      const own = ((form.ownMoto as string) ?? "yes") === "yes";
      return own && isComplete;
    }
    return isComplete;
  };

  if (mode === "vertical") {
    return (
      <div className={className}>
        <Card className="position-sticky mb-3" style={{ top: 16, zIndex: 1 }}>
          <Card.Header>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>
                  {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Section {activeIndex + 1} of {total}
                </strong>{" "}
                — {enabledSections[activeIndex]?.title}
              </div>
              <div style={{ minWidth: 180 }}>
                <ProgressBar now={pct} label={`${pctLabel}%`} />
              </div>
            </div>
          </Card.Header>
        </Card>

        {enabledSections.map((s) => {
          const isActive = activeId === s.id;
          const successVisual = sectionShowsSuccess(s.id);

          const cardClasses = [
            "border",
            successVisual ? "border-success bg-success-subtle" : "",
            isActive && !successVisual ? "border-primary" : "",
          ].join(" ").trim();

          const headerClasses = isActive ? "bg-primary text-white" : "";

          return (
            <Row key={s.id} className="mb-3" style={{ scrollMarginTop: 90 }}>
              <Col
                md={8}
                onClick={() => setActiveId(s.id)}
                onFocusCapture={() => setActiveId(s.id)}
              >
                <Card className={cardClasses}>
                  <Card.Header className={headerClasses}>
                    {s.title}
                    {successVisual && !isActive && <Badge bg="success" className="ms-2">{copy.buttons.completeBadge}</Badge>}
                    {successVisual && isActive && <Badge bg="light" text="dark" className="ms-2">{copy.buttons.completeBadge}</Badge>}
                  </Card.Header>
                  <Card.Body>{s.render(form, setForm)}</Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className={isActive ? "border border-primary" : "border"}>
                  <Card.Header className={isActive ? "bg-primary text-white" : ""}>
                    {copy.sectionHelpHeader}
                  </Card.Header>
                  <Card.Body>
                    <strong>{s.title}</strong>
                    <p className="mb-0">{s.explanation}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          );
        })}

        <div className="d-flex justify-content-end mt-3">
          <Button variant="success" onClick={() => onSubmit?.(form)} disabled={!!submitting}>
            {submitting ? "Processing..." : copy.buttons.submit}
          </Button>
        </div>
      </div>
    );
  }

  const step = enabledSections[current];
  const successVisualForStep = sectionShowsSuccess(step.id);
  const cardClasses = successVisualForStep ? "border border-success bg-success-subtle" : "";

  return (
    <Card className={`${className ?? ""} ${cardClasses}`}>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>
              {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Step {current + 1} of {total}
            </strong>{" "}
            — {step.title}
          </div>
          <div style={{ minWidth: 160 }}>
            <ProgressBar now={pct} label={`${pctLabel}%`} />
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {step.render(form, setForm)}

        <Card className="mt-3">
          <Card.Header>{copy.sectionHelpHeader}</Card.Header>
          <Card.Body>
            <strong>{step.title}</strong>
            <p className="mb-0">{step.explanation}</p>
          </Card.Body>
        </Card>

        <hr />
        <WizardControls
          onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
          onNext={() => setCurrent((c) => Math.min(total - 1, c + 1))}
          onSubmit={() => onSubmit?.(form)}
          isFirst={current === 0}
          isLast={current === total - 1}
          submitting={submitting}
          copy={copy}
        />
      </Card.Body>
    </Card>
  );
};

/* ============================================================
   Example Usage — Motorcycle Quote H2
============================================================ */
export const Motorcycle_QuoteWizard_H2a: React.FC<{ copy?: IMotoCopy }> = ({ copy = MOTO_COPY }) => {
  const [uiMode, setUiMode] = useState<ViewMode>("vertical");
  const [demoMode, setDemoMode] = useState<boolean>(DEMO_MODE_DEFAULT);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Initial base state (defaults)
  const initialBase: FormShape = { ownMoto: "yes", needFiling: "no" };

  // Demo data (VIN is 17 chars)
  const DEMO_DATA: FormShape = {
    ...initialBase,
    // Personal
    firstName: "Casey",
    lastName: "Rider",
    dob: "1990-05-20",
    email: "casey.rider@example.com",
    phone: "(720) 555-0199",
    address1: "456 Canyon Rd",
    address2: "Unit 12",
    city: "Boulder",
    state: "CO",
    zip: "80302",
    // Rider history
    licenseNumber: "R987654321",
    issuingState: "CO",
    yearsRiding: "8",
    msfCertified: "yes",
    safetyGear: "full",
    ticketsLast3Yrs: "1 speeding",
    atFaultAccidents: "0",
    needFiling: "no",
    // Filings
    filingType: "sr22",
    filingState: "CO",
    filingDuration: "3",
    // Motorcycle
    motoYear: "2021",
    motoMake: "Yamaha",
    motoModel: "MT-09",
    engineCC: "847",
    motoUse: "pleasure",
    annualMileage: "5000",
    motoVIN: "JYARN23E0MA000123",
    // Coverage/Remarks
    coverageType: "full",
    compDeductible: "500",
    remarks: "Weekend rider, garaged at home.",
  };

  // Sections
  const sections: IWizardSection[] = useMemo(() => {
    const personal: IWizardSection = {
      id: "personal-info",
      title: copy.personal.title,
      explanation: copy.personal.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group controlId="firstName">
                <Form.Label>{copy.personal.fields.firstName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.firstName as string) ?? ""}
                  onChange={(e) => setForm({ firstName: e.target.value })}
                  placeholder={copy.personal.fields.firstName.placeholder}
                  isValid={hasVal(form.firstName)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="lastName">
                <Form.Label>{copy.personal.fields.lastName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.lastName as string) ?? ""}
                  onChange={(e) => setForm({ lastName: e.target.value })}
                  placeholder={copy.personal.fields.lastName.placeholder}
                  isValid={hasVal(form.lastName)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="dob">
                <Form.Label>{copy.personal.fields.dob.label}</Form.Label>
                <Form.Control
                  type="date"
                  value={(form.dob as string) ?? ""}
                  onChange={(e) => setForm({ dob: e.target.value })}
                  isValid={hasVal(form.dob)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label>{copy.personal.fields.email.label}</Form.Label>
                <Form.Control
                  type="email"
                  value={(form.email as string) ?? ""}
                  onChange={(e) => setForm({ email: e.target.value })}
                  placeholder={copy.personal.fields.email.placeholder}
                  isValid={hasVal(form.email)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="phone">
                <Form.Label>{copy.personal.fields.phone.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.phone as string) ?? ""}
                  onChange={(e) => setForm({ phone: e.target.value })}
                  placeholder={copy.personal.fields.phone.placeholder}
                  inputMode="tel"
                  isValid={hasVal(form.phone)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="address1">
                <Form.Label>{copy.personal.fields.address1.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.address1 as string) ?? ""}
                  onChange={(e) => setForm({ address1: e.target.value })}
                  placeholder={copy.personal.fields.address1.placeholder}
                  autoComplete="address-line1"
                  isValid={hasVal(form.address1)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="address2">
                <Form.Label>{copy.personal.fields.address2.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.address2 as string) ?? ""}
                  onChange={(e) => setForm({ address2: e.target.value })}
                  placeholder={copy.personal.fields.address2.placeholder}
                  autoComplete="address-line2"
                  isValid={hasVal(form.address2)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="city">
                <Form.Label>{copy.personal.fields.city.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.city as string) ?? ""}
                  onChange={(e) => setForm({ city: e.target.value })}
                  isValid={hasVal(form.city)}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group controlId="state">
                <Form.Label>{copy.personal.fields.state.label}</Form.Label>
                <Form.Select
                  value={(form.state as string) ?? ""}
                  onChange={(e) => setForm({ state: e.target.value })}
                  isValid={hasVal(form.state)}
                >
                  <option value="">{copy.personal.fields.state.placeholder}</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group controlId="zip">
                <Form.Label>{copy.personal.fields.zip.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.zip as string) ?? ""}
                  onChange={(e) => setForm({ zip: e.target.value })}
                  placeholder={copy.personal.fields.zip.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.zip)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const riderHistory: IWizardSection = {
      id: "rider-history",
      title: copy.riderHistory.title,
      explanation: copy.riderHistory.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="licenseNumber">
                <Form.Label>{copy.riderHistory.fields.licenseNumber.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.licenseNumber as string) ?? ""}
                  onChange={(e) => setForm({ licenseNumber: e.target.value })}
                  placeholder={copy.riderHistory.fields.licenseNumber.placeholder}
                  isValid={hasVal(form.licenseNumber)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="issuingState">
                <Form.Label>{copy.riderHistory.fields.issuingState.label}</Form.Label>
                <Form.Select
                  value={(form.issuingState as string) ?? ""}
                  onChange={(e) => setForm({ issuingState: e.target.value })}
                  isValid={hasVal(form.issuingState)}
                >
                  <option value="">{copy.riderHistory.fields.issuingState.placeholder}</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="yearsRiding">
                <Form.Label>{copy.riderHistory.fields.yearsRiding.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.yearsRiding as string) ?? ""}
                  onChange={(e) => setForm({ yearsRiding: e.target.value })}
                  placeholder={copy.riderHistory.fields.yearsRiding.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.yearsRiding)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="msfCertified">
                <Form.Label>{copy.riderHistory.fields.msfCertified.label}</Form.Label>
                <Form.Select
                  value={(form.msfCertified as string) ?? ""}
                  onChange={(e) => setForm({ msfCertified: e.target.value })}
                  isValid={hasVal(form.msfCertified)}
                >
                  <option value="">{copy.riderHistory.fields.msfCertified.placeholder}</option>
                  <option value="yes">{copy.riderHistory.fields.msfCertified.options.yes}</option>
                  <option value="no">{copy.riderHistory.fields.msfCertified.options.no}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="safetyGear">
                <Form.Label>{copy.riderHistory.fields.safetyGear.label}</Form.Label>
                <Form.Select
                  value={(form.safetyGear as string) ?? ""}
                  onChange={(e) => setForm({ safetyGear: e.target.value })}
                  isValid={hasVal(form.safetyGear)}
                >
                  <option value="">{copy.riderHistory.fields.safetyGear.placeholder}</option>
                  <option value="full">{copy.riderHistory.fields.safetyGear.options.full}</option>
                  <option value="partial">{copy.riderHistory.fields.safetyGear.options.partial}</option>
                  <option value="helmetOnly">{copy.riderHistory.fields.safetyGear.options.helmetOnly}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="ticketsLast3Yrs">
                <Form.Label>{copy.riderHistory.fields.ticketsLast3Yrs.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.ticketsLast3Yrs as string) ?? ""}
                  onChange={(e) => setForm({ ticketsLast3Yrs: e.target.value })}
                  placeholder={copy.riderHistory.fields.ticketsLast3Yrs.placeholder}
                  isValid={hasVal(form.ticketsLast3Yrs)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="atFaultAccidents">
                <Form.Label>{copy.riderHistory.fields.atFaultAccidents.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.atFaultAccidents as string) ?? ""}
                  onChange={(e) => setForm({ atFaultAccidents: e.target.value })}
                  placeholder={copy.riderHistory.fields.atFaultAccidents.placeholder}
                  isValid={hasVal(form.atFaultAccidents)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const filings: IWizardSection = {
      id: "filings",
      title: copy.filings.title,
      explanation: copy.filings.help,
      enabled: true, // always render; inner fields toggle by needFiling
      render: (form, setForm) => {
        const needFiling = ((form.needFiling as string) ?? "no") === "yes";
        const filingType = (form.filingType as string) ?? "";
        const isFR44 = filingType === "fr44";
        const filingStates = isFR44 ? FR44_STATES : US_STATES;

        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="needFiling"
                  label={copy.filings.needFilingCheckbox}
                  checked={needFiling}
                  onChange={(e) => setForm({ needFiling: e.target.checked ? "yes" : "no" })}
                />
              </Col>

              {needFiling && (
                <>
                  <Col md={6}>
                    <Form.Group controlId="filingType">
                      <Form.Label>{copy.filings.fields.filingType.label}</Form.Label>
                      <Form.Select
                        value={filingType}
                        onChange={(e) => setForm({ filingType: e.target.value, filingState: "" })}
                        isValid={hasVal(filingType)}
                      >
                        <option value="">{copy.filings.fields.filingType.placeholder}</option>
                        <option value="sr22">{copy.filings.fields.filingType.options.sr22}</option>
                        <option value="fr44">{copy.filings.fields.filingType.options.fr44}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="filingState">
                      <Form.Label>{copy.filings.fields.filingState.label}</Form.Label>
                      <Form.Select
                        value={(form.filingState as string) ?? ""}
                        onChange={(e) => setForm({ filingState: e.target.value })}
                        isValid={hasVal(form.filingState)}
                      >
                        <option value="">{copy.filings.fields.filingState.placeholder}</option>
                        {filingStates.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </Form.Select>
                      {isFR44 && (
                        <div className="mt-1">
                          <Badge bg="info">Note</Badge>{" "}
                          {copy.filings.fr44NotePrefix}
                          <strong>{copy.filings.fr44NoteStates}</strong>.
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group controlId="filingDuration">
                      <Form.Label>{copy.filings.fields.filingDuration.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.filingDuration as string) ?? ""}
                        onChange={(e) => setForm({ filingDuration: e.target.value })}
                        placeholder={copy.filings.fields.filingDuration.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.filingDuration)}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        );
      },
    };

    const motorcycle: IWizardSection = {
      id: "motorcycle",
      title: copy.motorcycle.title,
      explanation: copy.motorcycle.help,
      enabled: true,
      render: (form, setForm) => {
        const own = ((form.ownMoto as string) ?? "yes") === "yes";
        const vin = ((form.motoVIN as string) ?? "").trim();
        const vinLen = vin.length;
        const vinInvalid = vinLen > 0 && vinLen !== 17;

        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="ownMoto"
                  label={copy.motorcycle.ownMotoCheckbox}
                  checked={own}
                  onChange={(e) => setForm({ ownMoto: e.target.checked ? "yes" : "no" })}
                />
              </Col>

              {own && (
                <>
                  <Col md={4}>
                    <Form.Group controlId="motoYear">
                      <Form.Label>{copy.motorcycle.fields.year.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.motoYear as string) ?? ""}
                        onChange={(e) => setForm({ motoYear: e.target.value })}
                        placeholder={copy.motorcycle.fields.year.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.motoYear)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="motoMake">
                      <Form.Label>{copy.motorcycle.fields.make.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.motoMake as string) ?? ""}
                        onChange={(e) => setForm({ motoMake: e.target.value })}
                        isValid={hasVal(form.motoMake)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="motoModel">
                      <Form.Label>{copy.motorcycle.fields.model.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.motoModel as string) ?? ""}
                        onChange={(e) => setForm({ motoModel: e.target.value })}
                        isValid={hasVal(form.motoModel)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="engineCC">
                      <Form.Label>{copy.motorcycle.fields.engineCC.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.engineCC as string) ?? ""}
                        onChange={(e) => setForm({ engineCC: e.target.value })}
                        placeholder={copy.motorcycle.fields.engineCC.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.engineCC)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="motoUse">
                      <Form.Label>{copy.motorcycle.fields.use.label}</Form.Label>
                      <Form.Select
                        value={(form.motoUse as string) ?? ""}
                        onChange={(e) => setForm({ motoUse: e.target.value })}
                        isValid={hasVal(form.motoUse)}
                      >
                        <option value="">{copy.motorcycle.fields.use.placeholder}</option>
                        <option value="commute">{copy.motorcycle.fields.use.options.commute}</option>
                        <option value="pleasure">{copy.motorcycle.fields.use.options.pleasure}</option>
                        <option value="business">{copy.motorcycle.fields.use.options.business}</option>
                        <option value="track">{copy.motorcycle.fields.use.options.track}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group controlId="motoVIN">
                      <div className="d-flex justify-content-between align-items-end">
                        <Form.Label className="mb-0">{copy.motorcycle.fields.vin.label}</Form.Label>
                        <small className={vinInvalid ? "text-danger" : "text-muted"}>
                          {copy.motorcycle.vinLengthPrefix}: {vinLen}/17{" "}
                          {vinInvalid ? "— " + copy.motorcycle.vinInvalid : ""}
                        </small>
                      </div>
                      <Form.Control
                        type="text"
                        value={(form.motoVIN as string) ?? ""}
                        onChange={(e) => setForm({ motoVIN: e.target.value.toUpperCase() })}
                        placeholder={copy.motorcycle.fields.vin.placeholder}
                        isInvalid={vinInvalid}
                        isValid={!vinInvalid && vinLen > 0}
                      />
                      {vinInvalid && (
                        <Form.Control.Feedback type="invalid">
                          {copy.motorcycle.vinInvalid}
                        </Form.Control.Feedback>
                      )}
                      <Form.Text muted>{copy.motorcycle.vinTip}</Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="annualMileage">
                      <Form.Label>{copy.motorcycle.fields.annualMileage.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.annualMileage as string) ?? ""}
                        onChange={(e) => setForm({ annualMileage: e.target.value })}
                        placeholder={copy.motorcycle.fields.annualMileage.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.annualMileage)}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        );
      },
    };

    const coverage: IWizardSection = {
      id: "coverage",
      title: copy.coverage.title,
      explanation: copy.coverage.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="coverageType">
                <Form.Label>{copy.coverage.fields.coverageType.label}</Form.Label>
                <Form.Select
                  value={(form.coverageType as string) ?? ""}
                  onChange={(e) => setForm({ coverageType: e.target.value })}
                  isValid={hasVal(form.coverageType)}
                >
                  <option value="">{copy.coverage.fields.coverageType.placeholder}</option>
                  <option value="liability">{copy.coverage.fields.coverageType.options.liability}</option>
                  <option value="full">{copy.coverage.fields.coverageType.options.full}</option>
                  <option value="custom">{copy.coverage.fields.coverageType.options.custom}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="compDeductible">
                <Form.Label>{copy.coverage.fields.compDeductible.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.compDeductible as string) ?? ""}
                  onChange={(e) => setForm({ compDeductible: e.target.value })}
                  placeholder={copy.coverage.fields.compDeductible.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.compDeductible)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const remarks: IWizardSection = {
      id: "remarks",
      title: copy.remarks.title,
      explanation: copy.remarks.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group controlId="remarks">
                <Form.Label>{copy.remarks.fields.remarks.label}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={(form.remarks as string) ?? ""}
                  onChange={(e) => setForm({ remarks: e.target.value })}
                  placeholder={copy.remarks.fields.remarks.placeholder}
                  isValid={hasVal(form.remarks)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    return [personal, riderHistory, filings, motorcycle, coverage, remarks];
  }, [copy]);

  const initialForm = demoMode ? DEMO_DATA : initialBase;

  const handleSubmit = async (payload: FormShape) => {
    setSubmitError(null);
    setSubmitting(true);

    const enabledIds = sections.filter(s => s.enabled !== false).map(s => s.id);
    const raw = overallCompletion(enabledIds, payload);
    const pctToSend = clampPct(raw);

    // Strings-only payload
    const customer_DATA = {
      // Meta
      productMode: "motorcycle",
      submittedAtIso: new Date().toISOString(),
      source: `Motorcycle Quote Wizard — ${VERSION}`,

      // Personal
      firstName: S(payload.firstName),
      lastName: S(payload.lastName),
      dob: S(payload.dob),
      email: S(payload.email),
      phone: S(payload.phone),
      address1: S(payload.address1),
      address2: S(payload.address2),
      city: S(payload.city),
      state: S(payload.state),
      zip: S(payload.zip),

      // Rider History
      licenseNumber: S(payload.licenseNumber),
      issuingState: S(payload.issuingState),
      yearsRiding: S(payload.yearsRiding),
      msfCertified: S(payload.msfCertified),
      safetyGear: S(payload.safetyGear),
      ticketsLast3Yrs: S(payload.ticketsLast3Yrs),
      atFaultAccidents: S(payload.atFaultAccidents),

      // Filings
      needFiling: S(payload.needFiling),
      filingType: S(payload.filingType),
      filingState: S(payload.filingState),
      filingDuration: S(payload.filingDuration),

      // Motorcycle
      ownMoto: S(payload.ownMoto),
      motoYear: S(payload.motoYear),
      motoMake: S(payload.motoMake),
      motoModel: S(payload.motoModel),
      engineCC: S(payload.engineCC),
      motoUse: S(payload.motoUse),
      annualMileage: S(payload.annualMileage),
      motoVIN: S(payload.motoVIN),

      // Coverage (optional)
      coverageType: S(payload.coverageType),
      compDeductible: S(payload.compDeductible),

      // Remarks (optional)
      remarks: S(payload.remarks),

      // Progress metric (STRING pre-submit)
      formCompletionPercent: String(pctToSend),

      // Legacy/meta fields to match your backend shape
      domainName: String(window.location.hostname),
      itemCategory: String("Customer.motorcycle.quote"),
      itemClass: String("quote.request"),
      itemType: String("legacy.motorcycle"),
      clientVersion: String(VERSION + ".moto"),
    };

    if (TEST_MODE) {
      console.log("TEST MODE — customer_DATA:", customer_DATA);
      setSubmitting(false);
      return;
    }

    try {
      const resp = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customer_DATA),
      });
      if (!resp.ok) throw new Error("network");
      setSubmitted(true);
    } catch {
      setSubmitError("Error: network error, try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container my-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Header className="fw-bold">{copy.submitThankYou.header}</Card.Header>
              <Card.Body>
                <p className="mb-0">{copy.submitThankYou.body}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container my-4">
      {/* Page Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h3 className="m-0">
          {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""}
        </h3>

        <div className="d-flex align-items-center gap-3">
          {/* DEMO MODE UI (optional) */}
          {DEMO_MODE_UI_ENABLED && (
            <Form.Check
              type="checkbox"
              id="demoModeToggle"
              label={copy.buttons.demoToggle}
              checked={demoMode}
              onChange={(e) => setDemoMode(e.target.checked)}
              disabled={submitting}
            />
          )}

          {/* View switches */}
          <div className="d-flex gap-2">
            <Button
              variant={uiMode === "wizard" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("wizard")}
              disabled={submitting}
            >
              {copy.buttons.wizardView}
            </Button>
            <Button
              variant={uiMode === "vertical" ? "primary" : "outline-primary"}
              onClick={() => setUiMode("vertical")}
              disabled={submitting}
            >
              {copy.buttons.verticalView}
            </Button>
          </div>
        </div>
      </div>

      {submitError && <Alert variant="danger" className="mb-3">{submitError}</Alert>}

      <DualViewWizard_Moto_H2
        key={demoMode ? "demo-on" : "demo-off"}
        mode={uiMode}
        sections={sections}
        initialForm={demoMode ? DEMO_DATA : initialBase}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitted={false}
        copy={copy}
      />
    </div>
  );
};
